import type {
	DealstackAiBlockerRole,
	DealstackAiBlockerStatus,
	DealstackAiCorrectionKind,
	DealstackAiCurrencySource,
	DealstackAiDiscardReason,
	DealstackAiEntityCategory,
	DealstackAiExecutionLabel,
	DealstackAiFieldValueSource,
	DealstackAiFxRateBasis,
	DealstackAiFxRateSource,
	DealstackAiLedgerMatchKind,
	DealstackAiQuoteStatus,
	DealstackAiReasonCode,
	DealstackAiReasonSeverity,
	DealstackAiSelectDisabledReason,
	DealstackAiTransactionFieldKey,
	DealstackAiTransactionQuickFilter,
	DealstackAiTransactionStatus,
	DealstackAiTypeUnconfirmedDocumentKind,
	DealstackAiWrittenOutcome,
	IngestionDecisionKind,
	TransactionTitle,
	TransactionType,
} from '@dealstack/shared/lib/backendClient';
import type { RowDateDisplaySource } from '../RowActions/RowActions.types';
import type { TransactionColumnKey } from './TransactionsPanel.types';

export const COLUMN_HEADER_KEYS: Record<TransactionColumnKey, string> = {
	issuer: 'dealstack_ai.issuer.column',
	buyer: 'dealstack_ai.transactions.field.buyer',
	seller: 'dealstack_ai.transactions.field.seller',
	type: 'dealstack_ai.transactions.field.type',
	document: 'dealstack_ai.transactions.column.document',
	security: 'dealstack_ai.transactions.column.security',
	units: 'dealstack_ai.transactions.column.units',
	date: 'dealstack_ai.transactions.column.date',
	currency: 'dealstack_ai.transactions.column.currency',
	cost: 'dealstack_ai.transactions.column.cost',
	status: 'dealstack_ai.transactions.column.status',
};

export const TRANSACTION_STATUS_KEYS: Record<DealstackAiTransactionStatus, string> = {
	ready_to_merge: 'dealstack_ai.transaction_status.ready_to_merge',
	needs_review: 'dealstack_ai.transaction_status.needs_review',
	potential_existing: 'dealstack_ai.transaction_status.potential_existing',
	existing: 'dealstack_ai.transaction_status.existing',
	invalid: 'dealstack_ai.transaction_status.invalid',
	confirming: 'dealstack_ai.transaction_status.confirming',
	created_in_dealstack: 'dealstack_ai.transaction_status.created_in_dealstack',
	discarded: 'dealstack_ai.transaction_status.discarded',
};

export const QUICK_FILTER_KEYS: Record<DealstackAiTransactionQuickFilter, string> = {
	all: 'dealstack_ai.transactions.quick_filter.all',
	ready_to_merge: 'dealstack_ai.transactions.quick_filter.ready_to_merge',
	needs_review: 'dealstack_ai.transactions.quick_filter.needs_review',
	potential_existing: 'dealstack_ai.transactions.quick_filter.potential_existing',
	invalid: 'dealstack_ai.transactions.quick_filter.invalid',
	discarded: 'dealstack_ai.transactions.quick_filter.discarded',
	in_dealstack: 'dealstack_ai.transactions.quick_filter.in_dealstack',
};

export const REASON_KEYS: Record<DealstackAiReasonCode, string> = {
	holder_missing: 'dealstack_ai.reason.holder_missing',
	units_missing: 'dealstack_ai.reason.units_missing',
	security_missing: 'dealstack_ai.reason.security_missing',
	seller_missing: 'dealstack_ai.reason.seller_missing',
	units_not_positive: 'dealstack_ai.reason.units_not_positive',
	near_existing: 'dealstack_ai.reason.near_existing',
	cost_mismatch: 'dealstack_ai.reason.cost_mismatch',
	number_not_verbatim: 'dealstack_ai.reason.number_not_verbatim',
	quote_unverified: 'dealstack_ai.reason.quote_unverified',
	image_only_evidence: 'dealstack_ai.reason.image_only_evidence',
	type_unknown: 'dealstack_ai.reason.type_unknown',
	loan_terms: 'dealstack_ai.reason.loan_terms',
	nominee_roles: 'dealstack_ai.reason.nominee_roles',
	disposal_unfolded: 'dealstack_ai.reason.disposal_unfolded',
	quote_not_verbatim: 'dealstack_ai.reason.quote_not_verbatim',
	document_not_evidence: 'dealstack_ai.reason.document_not_evidence',
	date_assumed: 'dealstack_ai.reason.date_assumed',
	date_missing: 'dealstack_ai.reason.date_missing',
	dates_differ: 'dealstack_ai.reason.dates_differ',
	date_from_sibling: 'dealstack_ai.reason.date_from_sibling',
	date_entered: 'dealstack_ai.reason.date_entered',
	conversion_target_missing: 'dealstack_ai.reason.conversion_target_missing',
	fx_at_document_date: 'dealstack_ai.reason.fx_at_document_date',
	fx_no_date: 'dealstack_ai.reason.fx_no_date',
	fx_overridden: 'dealstack_ai.reason.fx_overridden',
	fx_rate_missing: 'dealstack_ai.reason.fx_rate_missing',
	currency_ambiguous: 'dealstack_ai.reason.currency_ambiguous',
	entity_unresolved: 'dealstack_ai.reason.entity_unresolved',
	entity_removed: 'dealstack_ai.reason.entity_removed',
	issuer_inferred: 'dealstack_ai.reason.issuer_inferred',
	duplicate_disagrees: 'dealstack_ai.reason.duplicate_disagrees',
	holder_pending: 'dealstack_ai.reason.holder_pending',
	ledger_twins: 'dealstack_ai.reason.ledger_twins',
	likely_duplicate: 'dealstack_ai.reason.likely_duplicate',
	type_unconfirmed: 'dealstack_ai.reason.type_unconfirmed',
	fold_invalid: 'dealstack_ai.reason.fold_invalid',
	date_signing_stamp: 'dealstack_ai.reason.date_signing_stamp',
	ledger_exact_tie: 'dealstack_ai.reason.ledger_exact_tie',
	convention_unconfirmed: 'dealstack_ai.reason.convention_unconfirmed',
	partial_interest_transfer: 'dealstack_ai.reason.partial_interest_transfer',
	nominee_split_title: 'dealstack_ai.reason.nominee_split_title',
	nominee_vehicle_row: 'dealstack_ai.reason.nominee_vehicle_row',
	nominee_unattributed: 'dealstack_ai.reason.nominee_unattributed',
	awaiting_information: 'dealstack_ai.reason.awaiting_information',
	cost_missing: 'dealstack_ai.reason.cost_missing',
};

type SelectBlockCode = Exclude<DealstackAiSelectDisabledReason, DealstackAiReasonCode>;

const SELECT_BLOCK_KEYS: Record<SelectBlockCode, string> = {
	ledger_decision_required: 'dealstack_ai.select_block.ledger_decision_required',
	row_confirming: 'dealstack_ai.select_block.row_confirming',
	row_written: 'dealstack_ai.select_block.row_written',
	row_discarded: 'dealstack_ai.select_block.row_discarded',
};

const isSelectBlockCode = (reason: DealstackAiSelectDisabledReason): reason is SelectBlockCode =>
	reason in SELECT_BLOCK_KEYS;

/** A select block is a row reason or one of the four select-block codes. */
export const selectDisabledReasonKey = (reason: DealstackAiSelectDisabledReason): string =>
	isSelectBlockCode(reason) ? SELECT_BLOCK_KEYS[reason] : REASON_KEYS[reason];

export const REASON_SEVERITY_KEYS: Record<DealstackAiReasonSeverity, string> = {
	hard: 'dealstack_ai.transactions.severity.hard',
	blocking: 'dealstack_ai.transactions.severity.blocking',
	soft: 'dealstack_ai.transactions.severity.soft',
	info: 'dealstack_ai.transactions.severity.info',
};

export const RESOLVE_IN_KEYS: Record<DealstackAiEntityCategory, string> = {
	securities: 'dealstack_ai.transactions.resolve_in.securities',
	people: 'dealstack_ai.transactions.resolve_in.people',
	currencies: 'dealstack_ai.transactions.resolve_in.currencies',
};

/** The tag before a blocking entity's name. */
export const ENTITY_CATEGORY_TAG_KEYS: Record<DealstackAiEntityCategory, string> = {
	securities: 'dealstack_ai.transactions.blocking.tag.securities',
	people: 'dealstack_ai.transactions.blocking.tag.people',
	currencies: 'dealstack_ai.transactions.blocking.tag.currencies',
};

export const BLOCKER_ROLE_KEYS: Record<DealstackAiBlockerRole, string> = {
	security: 'dealstack_ai.transactions.blocking.role.security',
	conversion_target: 'dealstack_ai.transactions.blocking.role.conversion_target',
	buyer: 'dealstack_ai.transactions.blocking.role.buyer',
	seller: 'dealstack_ai.transactions.blocking.role.seller',
	bought_for: 'dealstack_ai.transactions.blocking.role.bought_for',
	sold_for: 'dealstack_ai.transactions.blocking.role.sold_for',
	issuer: 'dealstack_ai.transactions.blocking.role.issuer',
	currency: 'dealstack_ai.transactions.blocking.role.currency',
};

export const BLOCKER_STATUS_KEYS: Record<DealstackAiBlockerStatus, string> = {
	matched: 'dealstack_ai.transactions.blocking.status.matched',
	new: 'dealstack_ai.transactions.blocking.status.new',
	unmatched: 'dealstack_ai.transactions.blocking.status.unmatched',
	potential_match: 'dealstack_ai.transactions.blocking.status.potential_match',
	ignored: 'dealstack_ai.transactions.blocking.status.ignored',
	pending: 'dealstack_ai.transactions.blocking.status.pending',
	removed: 'dealstack_ai.transactions.blocking.status.removed',
};

export const CORRECTION_KIND_KEYS: Record<DealstackAiCorrectionKind, string> = {
	holder_pending: 'dealstack_ai.transactions.still_needed.holder_pending',
	entity_pick: 'dealstack_ai.transactions.still_needed.entity_pick',
	ledger_pick: 'dealstack_ai.transactions.still_needed.ledger_pick',
	type_pick: 'dealstack_ai.transactions.still_needed.type_pick',
	type_changed: 'dealstack_ai.transactions.still_needed.type_changed',
	date_confirmed: 'dealstack_ai.transactions.still_needed.date_confirmed',
	date_entered: 'dealstack_ai.transactions.still_needed.date_entered',
	fx_override: 'dealstack_ai.transactions.still_needed.fx_override',
	discard: 'dealstack_ai.transactions.still_needed.discard',
	cost_entered: 'dealstack_ai.transactions.still_needed.cost_entered',
	party_changed: 'dealstack_ai.transactions.still_needed.party_changed',
};

export const DISCARD_REASON_KEYS: Record<DealstackAiDiscardReason, string> = {
	invalid: 'dealstack_ai.discard_reason.invalid',
	duplicate: 'dealstack_ai.discard_reason.duplicate',
	not_a_transaction: 'dealstack_ai.discard_reason.not_a_transaction',
	wrong_document: 'dealstack_ai.discard_reason.wrong_document',
	extracted_value_wrong: 'dealstack_ai.discard_reason.extracted_value_wrong',
	other: 'dealstack_ai.discard_reason.other',
};

export const ROW_DATE_SOURCE_KEYS: Record<RowDateDisplaySource, string> = {
	printed: 'dealstack_ai.transactions.date.source.printed',
	assumed: 'dealstack_ai.transactions.date.source.assumed',
	sibling: 'dealstack_ai.transactions.date.source.sibling',
	entered: 'dealstack_ai.transactions.date.source.entered',
	confirmed: 'dealstack_ai.transactions.date.source.confirmed',
	missing: 'dealstack_ai.transactions.date.source.missing',
};

export const FIELD_KEYS: Record<DealstackAiTransactionFieldKey, string> = {
	buyer: 'dealstack_ai.transactions.field.buyer',
	seller: 'dealstack_ai.transactions.field.seller',
	boughtFor: 'dealstack_ai.transactions.field.bought_for',
	soldFor: 'dealstack_ai.transactions.field.sold_for',
	cost: 'dealstack_ai.transactions.field.cost',
	units: 'dealstack_ai.transactions.field.units',
	security: 'dealstack_ai.transactions.field.security',
	type: 'dealstack_ai.transactions.field.type',
	issuer: 'dealstack_ai.transactions.field.issuer',
	effectiveDate: 'dealstack_ai.transactions.field.effective_date',
	documentDate: 'dealstack_ai.transactions.field.document_date',
	pricePerUnit: 'dealstack_ai.transactions.field.price_per_unit',
	conversionTarget: 'dealstack_ai.transactions.field.conversion_target',
	rowDate: 'dealstack_ai.transactions.field.row_date',
};

export const FIELD_SOURCE_KEYS: Record<DealstackAiFieldValueSource, string> = {
	extracted: 'dealstack_ai.transactions.field_source.extracted',
	inferred_from_deal: 'dealstack_ai.transactions.field_source.inferred_from_deal',
	assumed: 'dealstack_ai.transactions.field_source.assumed',
	picked: 'dealstack_ai.transactions.field_source.picked',
	entered: 'dealstack_ai.transactions.field_source.entered',
	ledger_row: 'dealstack_ai.transactions.field_source.ledger_row',
	sibling: 'dealstack_ai.transactions.field_source.sibling',
};

/** Ledger types, plus the extractor's `UNKNOWN`. */
export const TRANSACTION_TYPE_KEYS: Record<TransactionType | 'UNKNOWN', string> = {
	TRANSFER: 'dealstack_ai.transactions.type.transfer',
	ISSUE: 'dealstack_ai.transactions.type.issue',
	REDEMPTION: 'dealstack_ai.transactions.type.redemption',
	CANCELLATION: 'dealstack_ai.transactions.type.cancellation',
	ISSUE_SPLIT: 'dealstack_ai.transactions.type.issue_split',
	REDEMPTION_REVERSE_SPLIT: 'dealstack_ai.transactions.type.redemption_reverse_split',
	TRANSFER_CONVERSION: 'dealstack_ai.transactions.type.transfer_conversion',
	SHAREHOLDER_CONTRIBUTION: 'dealstack_ai.transactions.type.shareholder_contribution',
	REDUCTION_IN_SHARE_CAPITAL: 'dealstack_ai.transactions.type.reduction_in_share_capital',
	CONVERSION_SHARE_CAPITAL: 'dealstack_ai.transactions.type.conversion_share_capital',
	DIVIDEND: 'dealstack_ai.transactions.type.dividend',
	VESTING_EVENT: 'dealstack_ai.transactions.type.vesting_event',
	UNKNOWN: 'dealstack_ai.transactions.type.unknown',
};

export const isTransactionTypeCode = (value: string): value is TransactionType | 'UNKNOWN' =>
	value in TRANSACTION_TYPE_KEYS;

export const TRANSACTION_TITLE_KEYS: Record<TransactionTitle, string> = {
	LEGAL_TITLE: 'dealstack_ai.transactions.title_kind.legal_title',
	BENEFICIAL_INTEREST: 'dealstack_ai.transactions.title_kind.beneficial_interest',
	BENEFICIAL_INTEREST_AND_LEGAL_TITLE:
		'dealstack_ai.transactions.title_kind.beneficial_interest_and_legal_title',
};

export const TYPE_UNCONFIRMED_KEYS: Record<DealstackAiTypeUnconfirmedDocumentKind, string> = {
	certificate: 'dealstack_ai.transactions.type_pick.because.certificate',
	tax_election: 'dealstack_ai.transactions.type_pick.because.tax_election',
};

export const WRITTEN_OUTCOME_KEYS: Record<DealstackAiWrittenOutcome, string> = {
	created: 'dealstack_ai.transactions.written.created',
	attached: 'dealstack_ai.transactions.written.attached',
	replaced: 'dealstack_ai.transactions.written.replaced',
};

export const DECISION_KIND_KEYS: Record<IngestionDecisionKind, string> = {
	create: 'dealstack_ai.transactions.decision.create',
	attach: 'dealstack_ai.transactions.decision.attach',
	replace: 'dealstack_ai.transactions.decision.replace',
	discard: 'dealstack_ai.transactions.decision.discard',
};

export const LEDGER_KIND_KEYS: Record<DealstackAiLedgerMatchKind, string> = {
	existing: 'dealstack_ai.transactions.ledger.kind.existing',
	potential_existing: 'dealstack_ai.transactions.ledger.kind.potential_existing',
	new: 'dealstack_ai.transactions.ledger.kind.new',
	pending: 'dealstack_ai.transactions.ledger.kind.pending',
};

/** Why a quote is not verified; `on_page` needs no words. */
export const QUOTE_STATUS_KEYS: Record<Exclude<DealstackAiQuoteStatus, 'on_page'>, string> = {
	not_on_page: 'dealstack_ai.transactions.evidence.quote_status.not_on_page',
	image_only: 'dealstack_ai.transactions.evidence.quote_status.image_only',
	not_verbatim: 'dealstack_ai.transactions.evidence.quote_status.not_verbatim',
};

export const EXECUTION_LABEL_KEYS: Record<DealstackAiExecutionLabel, string> = {
	executed: 'dealstack_ai.transactions.evidence.execution.executed',
	execution_not_confirmed: 'dealstack_ai.transactions.evidence.execution.execution_not_confirmed',
};

export const CURRENCY_SOURCE_KEYS: Record<DealstackAiCurrencySource, string> = {
	printed: 'dealstack_ai.transactions.cost.currency_source.printed',
	security: 'dealstack_ai.transactions.cost.currency_source.security',
};

export const FX_RATE_BASIS_KEYS: Record<DealstackAiFxRateBasis, string> = {
	effective_date: 'dealstack_ai.transactions.cost.rate_basis.effective_date',
	document_date: 'dealstack_ai.transactions.cost.rate_basis.document_date',
	sibling_date: 'dealstack_ai.transactions.cost.rate_basis.sibling_date',
	entered_date: 'dealstack_ai.transactions.cost.rate_basis.entered_date',
};

export const FX_RATE_SOURCE_KEYS: Record<DealstackAiFxRateSource, string> = {
	market_rate: 'dealstack_ai.transactions.cost.rate_source.market_rate',
	reviewer_override: 'dealstack_ai.transactions.cost.rate_source.reviewer_override',
};
